import { useMemo, useState } from 'react';

import config from '@/config';
import useQuery from '@/hooks/useQuery';
import ArtworkImage from '@/shared-components/cards/components/ArtworkImage';
import SearchNavbarIcon from '@/shared-components/icons/components/navigation/SearchNavbarIcon';
import XIcon from '@/shared-components/icons/components/public/XIcon';
import SearchField from '@/shared-components/inputs/components/SearchField';
import InfiniteScroll from '@/shared-components/lists/components/InfiniteScroll';
import Loader from '@/shared-components/loader/Loader';
import PopupToBottomSheet from '@/shared-components/popups/components/PopupToBottomSheet';
import Text from '@/shared-components/text/components/Text';
import { getArtworkArtistDisplayName } from '@/utils/artworkArtists';

import './styles/ProfileCoverPickerPopup.css';

export default function ProfileCoverPickerPopup({
  open,
  close,
  userId,
  userType = 'gallery',
  selectedImageId,
  onAddImage,
  onSelectArtworkImage
}) {
  const [searchValue, setSearchValue] = useState('');
  const query = useMemo(
    () => ({ sort: 'Recent', user_type: userType === 'artist' ? 'artist' : 'gallery', q: searchValue.trim() }),
    [searchValue, userType]
  );
  const { loading, queryData, loadMoreData } = useQuery(
    `edit_profile_${userType}_cover_artworks_${userId || 'unknown'}`,
    `${config.apis.api.url}/artworks/${userId}`,
    query,
    { injectToken: true }
  );

  const artworksData = queryData?.data;
  const artworks = artworksData?.data || [];
  const canLoadMore = artworks.length < (artworksData?.totalDocs || 0);

  return (
    <PopupToBottomSheet
      className="profile-cover-picker-popup-bottom-sheet__container"
      open={open}
      close={close}
      popupOptions={{ opacity: true }}
    >
      <div className="profile-cover-picker__content-container">
        <div className="profile-cover-picker__header">
          <button
            type="button"
            className="profile-cover-picker__add-image-button"
            onClick={onAddImage}
          >
            Add image
          </button>

          <Text className="profile-cover-picker__header-text font-size-large">
            Change cover
          </Text>

          <XIcon className="profile-cover-picker__close-button" onClick={close} />
        </div>

        <SearchField
          className="profile-cover-picker__search-bar"
          leading={<SearchNavbarIcon />}
          value={searchValue}
          onChange={(event) => setSearchValue(event.target.value)}
          onClear={() => setSearchValue('')}
          placeholder="Search artworks"
          spellCheck={false}
          autoComplete="off"
        />

        <div className="profile-cover-picker__list-container">
          {loading && artworks.length === 0 ? (
            <div className="profile-cover-picker__loading-state">
              <Loader thin />
            </div>
          ) : artworks.length === 0 ? (
            <Text className="profile-cover-picker__empty-text font-size-small" paragraph>
              No artworks found.
            </Text>
          ) : (
            <InfiniteScroll
              className="profile-cover-picker__artworks-list remove-scrollbar"
              onLoadMore={loadMoreData}
              shouldLoadMore={canLoadMore}
              offsetElements={3}
              listLength={artworks.length}
              defaultInfiniteLoaderClassName="profile-cover-picker__virtual-spinner"
            >
              {({ infiniteScrollAssigner }) => (
                <>
                  {artworks.map((artwork, index) => {
                    const mainPicture = artwork?.artwork_media?.artwork_main_picture;
                    const imageId = mainPicture?.image_id;
                    if (!imageId) return null;

                    const title = artwork?.artwork_about?.artwork_title || 'Untitled';
                    const resolvedArtistName = getArtworkArtistDisplayName(artwork);
                    const artistName = resolvedArtistName === '-' ? '' : resolvedArtistName;
                    const isActive = selectedImageId === imageId;

                    return (
                      <button
                        key={artwork._id || imageId}
                        ref={infiniteScrollAssigner(index)}
                        type="button"
                        className={`profile-cover-picker__artwork-row${isActive ? ' active' : ''}`}
                        onClick={() => onSelectArtworkImage(mainPicture)}
                      >
                        <div className="profile-cover-picker__artwork-info">
                          <ArtworkImage
                            image={imageId}
                            imageClassName="profile-cover-picker__artwork-image"
                            imageTemplateClassName="profile-cover-picker__artwork-image"
                            enabledDoubleClick={false}
                          />
                          <div className="profile-cover-picker__artwork-text">
                            <Text className="profile-cover-picker__artwork-title font-size-medium" paragraph>
                              {title}
                            </Text>
                            {Boolean(artistName) && (
                              <Text className="profile-cover-picker__artwork-artist font-size-small" paragraph>
                                By {artistName}
                              </Text>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </>
              )}
            </InfiniteScroll>
          )}
        </div>
      </div>
    </PopupToBottomSheet>
  );
}
